import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowDownRight,
    ArrowUpRight,
    BarChart3,
    ChevronDown,
    ChevronUp,
    Info,
    Loader2,
    PieChart as PieIcon,
    RefreshCw,
    Search,
    TrendingDown,
    TrendingUp,
    Wallet,
} from "lucide-react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { operasyonHub } from "@/lib/api";

const CHART_COLORS = ["#D4AF37", "#34D399", "#60A5FA", "#F472B6", "#A78BFA", "#FB923C", "#94A3B8"];

function fmt(n, opts = {}) {
    return `₺${Number(n || 0).toLocaleString("tr-TR", {
        maximumFractionDigits: opts.decimals ?? 0,
        ...opts,
    })}`;
}

function fmtPct(n) {
    if (n == null || Number.isNaN(n)) return "—";
    const v = Number(n);
    return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
}

function DeltaBadge({ value, label }) {
    if (value == null) return null;
    const up = Number(value) >= 0;
    return (
        <span
            className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                up ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"
            }`}
            title={label}
        >
            {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {fmtPct(value)}
        </span>
    );
}

function KpiCard({ label, value, sub, hint, delta, tone = "gold", icon: Icon }) {
    const tones = {
        gold: "border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/8 to-transparent text-[#F3E5AB]",
        green: "border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent text-emerald-300",
        rose: "border-rose-500/30 bg-gradient-to-br from-rose-500/10 to-transparent text-rose-300",
        blue: "border-sky-500/30 bg-gradient-to-br from-sky-500/10 to-transparent text-sky-300",
        violet: "border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-transparent text-violet-300",
    };
    const t = tones[tone] || tones.gold;
    return (
        <div className={`rounded-2xl border p-4 ${t.split(" ").slice(0, 3).join(" ")}`}>
            <div className="flex items-start justify-between gap-2">
                <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                        {label}
                        {hint ? (
                            <span title={hint} className="cursor-help">
                                <Info className="h-3 w-3 text-slate-600" />
                            </span>
                        ) : null}
                    </p>
                    <p className={`text-2xl font-bold tabular-nums mt-1 ${t.split(" ").slice(3).join(" ")}`}>
                        {value}
                    </p>
                </div>
                {Icon ? <Icon className="h-5 w-5 opacity-40 shrink-0" /> : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
                {delta != null ? <DeltaBadge value={delta} label="Önceki 30 güne göre" /> : null}
                {sub ? <p className="text-[11px] text-slate-500">{sub}</p> : null}
            </div>
        </div>
    );
}

function ChartTooltip({ active, payload, label, formatter }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-white/10 bg-[#0B0F19] px-3 py-2 text-xs shadow-xl">
            <p className="text-slate-400 mb-1">{label}</p>
            {payload.map((p) => (
                <p key={p.name} style={{ color: p.color }}>
                    {p.name}: {formatter ? formatter(p.value) : p.value}
                </p>
            ))}
        </div>
    );
}

/** Recharts ResponsiveContainer needs a parent with explicit pixel height. */
function ChartBox({ height = 260, children, ready = true }) {
    if (!ready) {
        return (
            <div
                className="w-full min-w-0 flex items-center justify-center text-slate-600 text-xs"
                style={{ height }}
            >
                Grafik yükleniyor…
            </div>
        );
    }
    return (
        <div className="w-full min-w-0" style={{ height, minHeight: height }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} debounce={48}>
                {children}
            </ResponsiveContainer>
        </div>
    );
}

function ChartCard({ title, subtitle, height = 260, ready, children, className = "" }) {
    return (
        <div className={`rounded-2xl border border-white/[0.08] bg-[#0a1018]/80 p-4 flex flex-col min-w-0 ${className}`}>
            {title ? <div className="text-sm font-semibold text-white mb-1 shrink-0">{title}</div> : null}
            {subtitle ? <p className="text-[10px] text-slate-500 mb-3 shrink-0">{subtitle}</p> : null}
            <ChartBox height={height} ready={ready}>
                {children}
            </ChartBox>
        </div>
    );
}

function DosyaPlTable({ rows, title, tone = "default", emptyMsg }) {
    const [sort, setSort] = useState({ key: "net_kar", dir: "desc" });
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState(null);

    const filtered = useMemo(() => {
        let list = [...(rows || [])];
        if (q.trim()) {
            const s = q.toLowerCase();
            list = list.filter(
                (r) =>
                    r.dosya_no?.toLowerCase().includes(s) ||
                    r.muvekkil?.toLowerCase().includes(s) ||
                    r.plaka?.toLowerCase().includes(s)
            );
        }
        list.sort((a, b) => {
            const av = a[sort.key] ?? 0;
            const bv = b[sort.key] ?? 0;
            return sort.dir === "asc" ? av - bv : bv - av;
        });
        return list;
    }, [rows, q, sort]);

    const toggleSort = (key) => {
        setSort((prev) =>
            prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }
        );
    };

    const SortHead = ({ k, children, align = "left" }) => (
        <th
            className={`px-3 py-2.5 cursor-pointer hover:text-white select-none text-${align}`}
            onClick={() => toggleSort(k)}
        >
            <span className="inline-flex items-center gap-1">
                {children}
                {sort.key === k ? (
                    sort.dir === "asc" ? (
                        <ChevronUp className="h-3 w-3" />
                    ) : (
                        <ChevronDown className="h-3 w-3" />
                    )
                ) : null}
            </span>
        </th>
    );

    return (
        <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    {tone === "rose" ? (
                        <TrendingDown className="h-5 w-5 text-rose-400" />
                    ) : (
                        <TrendingUp className="h-5 w-5 text-[#D4AF37]" />
                    )}
                    {title}
                </h2>
                <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Dosya, müvekkil, plaka…"
                        className="h-9 pl-9 pr-3 rounded-xl bg-[#0a1018] border border-white/10 text-sm text-white w-56"
                    />
                </div>
            </div>
            <div className="rounded-2xl border border-white/[0.08] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[900px]">
                        <thead className="bg-white/[0.03] text-[10px] uppercase text-slate-500">
                            <tr>
                                <SortHead k="dosya_no">Dosya</SortHead>
                                <SortHead k="muvekkil">Müvekkil</SortHead>
                                <th className="text-left px-3 py-2.5">Durum</th>
                                <SortHead k="mbs_hakedis_toplam" align="right">
                                    MBS
                                </SortHead>
                                <SortHead k="masraf_toplam" align="right">
                                    Masraf
                                </SortHead>
                                <SortHead k="net_kar" align="right">
                                    Net
                                </SortHead>
                                <SortHead k="marj_yuzde" align="right">
                                    Marj
                                </SortHead>
                                <SortHead k="deger_kaybi_tutari" align="right">
                                    DK
                                </SortHead>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.04]">
                            {!filtered.length ? (
                                <tr>
                                    <td colSpan={8} className="text-center py-10 text-slate-500">
                                        {emptyMsg || "Kayıt yok"}
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((row) => (
                                    <React.Fragment key={row.dosya_id}>
                                        <tr
                                            className="hover:bg-white/[0.02] cursor-pointer"
                                            onClick={() =>
                                                setExpanded((e) => (e === row.dosya_id ? null : row.dosya_id))
                                            }
                                        >
                                            <td className="px-3 py-2.5">
                                                <Link
                                                    to={`/e/sigorta-dosyalari/${row.dosya_id}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-[#D4AF37] hover:underline font-mono text-xs"
                                                >
                                                    {row.dosya_no}
                                                </Link>
                                                <span className="text-slate-600 text-xs ml-1">{row.plaka}</span>
                                            </td>
                                            <td className="px-3 py-2.5 text-slate-300 max-w-[160px] truncate">
                                                {row.muvekkil}
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400">
                                                    {row.dosya_durumu_etiket || row.dosya_durumu || "—"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-300">
                                                {fmt(row.mbs_hakedis_toplam)}
                                            </td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-rose-300/80">
                                                {fmt(row.masraf_toplam)}
                                            </td>
                                            <td
                                                className={`px-3 py-2.5 text-right tabular-nums font-semibold ${
                                                    row.net_kar >= 0 ? "text-emerald-400" : "text-rose-400"
                                                }`}
                                            >
                                                {fmt(row.net_kar)}
                                            </td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-400 text-xs">
                                                {row.marj_yuzde != null ? `%${row.marj_yuzde}` : "—"}
                                            </td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-500 text-xs">
                                                {row.deger_kaybi_tutari ? fmt(row.deger_kaybi_tutari) : "—"}
                                            </td>
                                        </tr>
                                        {expanded === row.dosya_id ? (
                                            <tr className="bg-black/20">
                                                <td colSpan={8} className="px-4 py-3">
                                                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 text-xs">
                                                        <div>
                                                            <span className="text-slate-500">Gelen ödeme</span>
                                                            <p className="text-slate-200 font-medium tabular-nums">
                                                                {fmt(row.gelen_odeme_toplam)}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-slate-500">Müvekkil hakediş</span>
                                                            <p className="text-slate-200 font-medium tabular-nums">
                                                                {fmt(row.muvekkil_hakedis_toplam)}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-slate-500">Kasa gelen/giden</span>
                                                            <p className="text-slate-200 font-medium tabular-nums">
                                                                {fmt(row.kasa_gelen)} / {fmt(row.kasa_giden)}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-slate-500">Ödeme kayıt</span>
                                                            <p className="text-slate-200 font-medium">
                                                                {row.odeme_kayit_sayisi}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-slate-500">ROI (net/masraf)</span>
                                                            <p className="text-slate-200 font-medium">
                                                                {row.roi != null ? `%${row.roi}` : "—"}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <span className="text-slate-500">Avukat</span>
                                                            <p className="text-slate-200 font-medium truncate">
                                                                {row.avukat || "—"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : null}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}

export default function FinansKarZararPage() {
    const [tab, setTab] = useState("dashboard");
    const [yil, setYil] = useState(new Date().getFullYear());
    const [data, setData] = useState(null);
    const [yillikData, setYillikData] = useState(null);
    const [bilancoData, setBilancoData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chartsReady, setChartsReady] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ozet, yillik, bilanco] = await Promise.all([
                operasyonHub.finans({ scope: "ozet" }),
                operasyonHub.finans({ scope: "yillik", yil }),
                operasyonHub.finans({ scope: "bilanco" }),
            ]);
            setData(ozet);
            setYillikData(yillik);
            setBilancoData(bilanco);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [yil]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (loading) {
            setChartsReady(false);
            return undefined;
        }
        const id = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => setChartsReady(true));
        });
        return () => window.cancelAnimationFrame(id);
    }, [loading, tab, data, yillikData, bilancoData]);

    const TABS = [
        { id: "dashboard", label: "Genel Bakış", desc: "KPI, trend, dağılım" },
        { id: "dosyalar", label: "Dosya P&L", desc: "Karlı / zararlı dosyalar" },
        { id: "yillik", label: "Yıllık", desc: "Aylık K/Z trendi" },
        { id: "bilanco", label: "Bilanço", desc: "Kasa & hesaplar" },
        { id: "analiz", label: "Analiz", desc: "Durum & masraf kırılımı" },
    ];

    const d30 = data?.donem_30_gun;
    const d7 = data?.donem_7_gun;
    const deg = data?.degisim_30_gun;
    const kpi = data?.kpi;

    return (
        <div className="max-w-screen-2xl mx-auto space-y-6 pb-20 px-4">
            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Finans Hub · v2</p>
                    <h1 className="font-display text-3xl text-white mt-1">Kar / Zarar Merkezi</h1>
                    <p className="text-sm text-slate-500 mt-1 max-w-2xl leading-relaxed">
                        Dosya bazlı P&L, marj analizi, tahsilat bekleyenleri, durum kırılımı ve kasa bilançosu —
                        tek ekranda ileri düzey finans istihbaratı.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {tab === "yillik" ? (
                        <select
                            value={yil}
                            onChange={(e) => setYil(Number(e.target.value))}
                            className="h-10 rounded-xl bg-[#0a1018] border border-white/10 px-3 text-sm text-white"
                        >
                            {[0, 1, 2, 3].map((i) => {
                                const y = new Date().getFullYear() - i;
                                return (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                );
                            })}
                        </select>
                    ) : null}
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="h-10 px-4 rounded-xl bg-[#D4AF37] text-slate-900 text-sm font-bold inline-flex items-center gap-2"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        Yenile
                    </button>
                </div>
            </header>

            <nav className="flex gap-2 flex-wrap">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`px-4 py-2.5 rounded-xl text-left transition-colors ${
                            tab === t.id
                                ? "bg-white text-slate-900 shadow-lg"
                                : "text-slate-400 hover:bg-white/5 border border-transparent hover:border-white/10"
                        }`}
                    >
                        <span className="text-sm font-semibold block">{t.label}</span>
                        <span
                            className={`text-[10px] ${tab === t.id ? "text-slate-600" : "text-slate-600"}`}
                        >
                            {t.desc}
                        </span>
                    </button>
                ))}
            </nav>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-24 gap-3">
                    <Loader2 className="h-10 w-10 animate-spin text-[#D4AF37]" />
                    <p className="text-sm text-slate-500">Finans verileri hesaplanıyor…</p>
                </div>
            ) : null}

            {!loading && tab === "dashboard" && data ? (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                        <KpiCard
                            label="30 gün net kâr"
                            value={fmt(d30?.net_kar)}
                            hint="MBS hakediş − masraf (son 30 gün)"
                            delta={deg?.net_kar_yuzde}
                            tone={d30?.net_kar >= 0 ? "green" : "rose"}
                            icon={TrendingUp}
                        />
                        <KpiCard
                            label="30 gün MBS hakediş"
                            value={fmt(d30?.mbs_hakedis)}
                            sub={`Marj %${d30?.marj_yuzde ?? 0}`}
                            delta={deg?.mbs_yuzde}
                            icon={Wallet}
                        />
                        <KpiCard
                            label="30 gün masraf"
                            value={fmt(d30?.masraf)}
                            sub={`Oran %${d30?.masraf_orani_yuzde ?? 0} of MBS`}
                            delta={deg?.masraf_yuzde}
                            tone="rose"
                        />
                        <KpiCard
                            label="7 gün net"
                            value={fmt(d7?.net_kar)}
                            sub={`${d7?.odeme_kayit_sayisi ?? 0} ödeme kaydı`}
                            tone={d7?.net_kar >= 0 ? "green" : "rose"}
                        />
                        <KpiCard
                            label="Toplam kasa"
                            value={fmt(data.bilanco?.toplam_kasa)}
                            sub={`Yıllık net ${fmt(data.yillik_ozet?.net_kar)}`}
                            tone="blue"
                            icon={Wallet}
                        />
                        <KpiCard
                            label="Bekleyen tahsilat"
                            value={fmt(kpi?.bekleyen_tutar)}
                            sub={`${kpi?.kayit_sayisi ?? 0} kayıt · MBS ${fmt(kpi?.bekleyen_mbs)}`}
                            tone="violet"
                            hint="Ödeme yapılmadı işaretli kayıtlar"
                        />
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <KpiCard
                            label="Karlı dosya"
                            value={kpi?.karli_dosya ?? 0}
                            sub={`Toplam ${kpi?.toplam_finans_dosya ?? 0} finans dosyası`}
                            tone="green"
                        />
                        <KpiCard
                            label="Zararlı dosya"
                            value={kpi?.zararli_dosya ?? 0}
                            tone={kpi?.zararli_dosya ? "rose" : "gold"}
                        />
                        <KpiCard
                            label="Ort. dosya net (30g)"
                            value={fmt(d30?.ort_dosya_net)}
                            sub={`${d30?.dosya_sayisi ?? 0} dosyada hareket`}
                        />
                        <KpiCard
                            label="DK potansiyel (toplam)"
                            value={fmt(kpi?.toplam_dk_potansiyel)}
                            hint="Açık dosyalardaki hesaplanan değer kaybı toplamı"
                            tone="blue"
                        />
                    </div>

                    <div className="grid lg:grid-cols-3 gap-4">
                        <ChartCard
                            className="lg:col-span-2"
                            title={
                                <span className="flex items-center gap-2">
                                    <BarChart3 className="h-4 w-4 text-[#D4AF37]" />
                                    30 günlük net kâr trendi
                                </span>
                            }
                            subtitle="Günlük MBS − masraf"
                            height={268}
                            ready={chartsReady}
                        >
                            <AreaChart data={data.gunluk_trend || []}>
                                <defs>
                                    <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#34D399" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="#34D399" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                                <XAxis dataKey="tarih_kisa" tick={{ fill: "#64748b", fontSize: 10 }} />
                                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} />
                                <Area
                                    type="monotone"
                                    dataKey="net_kar"
                                    name="Net kâr"
                                    stroke="#34D399"
                                    fill="url(#netGrad)"
                                    strokeWidth={2}
                                />
                            </AreaChart>
                        </ChartCard>

                        <ChartCard title="Son 6 ay net" height={268} ready={chartsReady}>
                            <BarChart data={data.aylik_mini || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                                <XAxis dataKey="ay_adi" tick={{ fill: "#64748b", fontSize: 10 }} />
                                <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} />
                                <Bar dataKey="net_kar" name="Net" radius={[4, 4, 0, 0]}>
                                    {(data.aylik_mini || []).map((e, i) => (
                                        <Cell key={i} fill={e.net_kar >= 0 ? "#34D399" : "#F87171"} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ChartCard>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-4">
                        <ChartCard title="MBS vs Masraf (30 gün)" height={248} ready={chartsReady}>
                            <LineChart data={data.gunluk_trend || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                                <XAxis dataKey="tarih_kisa" tick={{ fill: "#64748b", fontSize: 10 }} />
                                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Line type="monotone" dataKey="mbs_hakedis" name="MBS" stroke="#D4AF37" dot={false} strokeWidth={2} />
                                <Line type="monotone" dataKey="masraf" name="Masraf" stroke="#F87171" dot={false} strokeWidth={2} />
                            </LineChart>
                        </ChartCard>

                        <ChartCard
                            title={
                                <span className="flex items-center gap-2">
                                    <PieIcon className="h-4 w-4 text-[#D4AF37]" />
                                    Ödeme türü (30 gün)
                                </span>
                            }
                            height={248}
                            ready={chartsReady}
                        >
                            <PieChart>
                                <Pie
                                    data={data.odeme_turu || []}
                                    dataKey="toplam"
                                    nameKey="tur_etiket"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={45}
                                    outerRadius={75}
                                    paddingAngle={2}
                                >
                                    {(data.odeme_turu || []).map((_, i) => (
                                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(v) => fmt(v)} />
                                <Legend wrapperStyle={{ fontSize: 10 }} />
                            </PieChart>
                        </ChartCard>
                    </div>
                </div>
            ) : null}

            {!loading && tab === "dosyalar" && data ? (
                <div className="space-y-8">
                    <DosyaPlTable
                        rows={data.dosya_pl_top}
                        title="En karlı dosyalar (net kâr)"
                        emptyMsg="Henüz finans hareketi olan dosya yok"
                    />
                    {(data.dosya_pl_zarar || []).some((r) => r.net_kar < 0) ? (
                        <DosyaPlTable
                            rows={data.dosya_pl_zarar}
                            title="Zarar / düşük marjlı dosyalar"
                            tone="rose"
                        />
                    ) : null}
                </div>
            ) : null}

            {!loading && tab === "yillik" && yillikData ? (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <KpiCard label={`${yil} toplam net`} value={fmt(yillikData.toplam?.net_kar)} tone="green" />
                        <KpiCard label="MBS hakediş" value={fmt(yillikData.toplam?.mbs_hakedis)} />
                        <KpiCard label="Masraf" value={fmt(yillikData.toplam?.masraf)} tone="rose" />
                        <KpiCard label="Gelen ödeme" value={fmt(yillikData.toplam?.gelen_odeme)} tone="blue" />
                    </div>
                    <ChartCard title={`${yil} aylık K/Z`} height={320} ready={chartsReady}>
                        <BarChart data={yillikData.aylik || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                            <XAxis dataKey="ay_adi" tick={{ fill: "#64748b", fontSize: 11 }} />
                            <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="mbs_hakedis" name="MBS" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="masraf" name="Masraf" fill="#F87171" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="net_kar" name="Net" fill="#34D399" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ChartCard>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {(yillikData.aylik || []).map((a) => (
                            <div
                                key={a.ay}
                                className="rounded-xl border border-white/[0.08] bg-[#0a1018]/60 p-4"
                            >
                                <p className="text-sm font-semibold text-white">{a.ay_adi}</p>
                                <p
                                    className={`text-2xl font-bold tabular-nums mt-2 ${
                                        a.net_kar >= 0 ? "text-emerald-400" : "text-rose-400"
                                    }`}
                                >
                                    {fmt(a.net_kar)}
                                </p>
                                <div className="mt-2 space-y-1 text-[10px] text-slate-500">
                                    <p>MBS {fmt(a.mbs_hakedis)} · Marj %{a.marj_yuzde ?? 0}</p>
                                    <p>Masraf {fmt(a.masraf)} · {a.odeme_kayit_sayisi} kayıt</p>
                                    <p>{a.dosya_sayisi ?? 0} dosyada hareket</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {!loading && tab === "bilanco" && bilancoData ? (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <KpiCard
                            label="Toplam kasa bakiyesi"
                            value={fmt(bilancoData.toplam_kasa)}
                            tone="gold"
                            icon={Wallet}
                        />
                        <KpiCard
                            label="Yıllık net kâr"
                            value={fmt(bilancoData.yillik_ozet?.net_kar)}
                            tone="green"
                        />
                        <KpiCard
                            label="Yıllık MBS"
                            value={fmt(bilancoData.yillik_ozet?.mbs_hakedis)}
                            sub={`Masraf ${fmt(bilancoData.yillik_ozet?.masraf)}`}
                        />
                    </div>
                    <div className="rounded-2xl border border-white/[0.08] overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-white/[0.03] text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="text-left px-4 py-3">Hesap</th>
                                    <th className="text-left px-4 py-3">Para birimi</th>
                                    <th className="text-right px-4 py-3">Bakiye</th>
                                    <th className="text-right px-4 py-3">Pay</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {(bilancoData.hesaplar || []).map((h) => {
                                    const pay =
                                        bilancoData.toplam_kasa > 0
                                            ? ((h.bakiye / bilancoData.toplam_kasa) * 100).toFixed(1)
                                            : 0;
                                    return (
                                        <tr key={h.id} className="hover:bg-white/[0.02]">
                                            <td className="px-4 py-3 text-slate-200 flex items-center gap-2">
                                                <Wallet className="h-4 w-4 text-[#D4AF37]/70" />
                                                {h.ad}
                                            </td>
                                            <td className="px-4 py-3 text-slate-500">{h.para_birimi}</td>
                                            <td className="px-4 py-3 text-right tabular-nums text-[#F3E5AB] font-semibold">
                                                {fmt(h.bakiye)}
                                            </td>
                                            <td className="px-4 py-3 text-right text-slate-500 text-xs">%{pay}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Link
                        to="/yeni/kasa-hesaplar"
                        className="inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:underline"
                    >
                        <TrendingUp className="h-4 w-4" />
                        Kasa & hesap yönetimi →
                    </Link>
                </div>
            ) : null}

            {!loading && tab === "analiz" && data ? (
                <div className="space-y-6">
                    <div className="grid lg:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-white/[0.08] bg-[#0a1018]/80 p-4">
                            <h3 className="text-sm font-semibold text-white mb-3">Dosya durumuna göre net kâr</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-[10px] uppercase text-slate-500">
                                        <tr>
                                            <th className="text-left py-2">Durum</th>
                                            <th className="text-right py-2">Dosya</th>
                                            <th className="text-right py-2">Net</th>
                                            <th className="text-right py-2">Marj</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/[0.04]">
                                        {(data.durum_dagilimi || []).map((r) => (
                                            <tr key={r.durum}>
                                                <td className="py-2 text-slate-300">{r.durum_etiket}</td>
                                                <td className="py-2 text-right text-slate-500">{r.dosya_sayisi}</td>
                                                <td
                                                    className={`py-2 text-right tabular-nums font-medium ${
                                                        r.net_kar >= 0 ? "text-emerald-400" : "text-rose-400"
                                                    }`}
                                                >
                                                    {fmt(r.net_kar)}
                                                </td>
                                                <td className="py-2 text-right text-slate-500 text-xs">
                                                    %{r.marj_yuzde}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <ChartCard title="Masraf kalemleri (30 gün)" height={280} ready={chartsReady}>
                            <PieChart>
                                <Pie
                                    data={data.masraf_kalemleri || []}
                                    dataKey="toplam"
                                    nameKey="kalem"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    label={({ kalem, percent }) =>
                                        `${kalem?.slice(0, 12)} ${(percent * 100).toFixed(0)}%`
                                    }
                                    labelLine={false}
                                >
                                    {(data.masraf_kalemleri || []).map((_, i) => (
                                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(v) => fmt(v)} />
                            </PieChart>
                        </ChartCard>
                    </div>

                    <ChartCard title="Durum bazlı MBS dağılımı" height={300} ready={chartsReady}>
                        <BarChart data={data.durum_dagilimi || []} layout="vertical" margin={{ left: 80 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                            <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <YAxis type="category" dataKey="durum_etiket" tick={{ fill: "#94a3b8", fontSize: 10 }} width={75} />
                            <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} />
                            <Bar dataKey="mbs_hakedis" name="MBS" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                            <Bar dataKey="masraf" name="Masraf" fill="#F87171" radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ChartCard>

                    <div className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-4 text-xs text-slate-400 leading-relaxed">
                        <strong className="text-[#F3E5AB]">Metodoloji:</strong> Net kâr = MBS Hukuk hakedişi − toplam
                        masraf. Marj = net / MBS × 100. 30 günlük değişim, bir önceki 30 günlük döneme göre
                        hesaplanır. Bekleyen tahsilat, &quot;ödeme yapılmadı&quot; işaretli kayıtların tutarıdır.
                    </div>
                </div>
            ) : null}
        </div>
    );
}
